import { getFirebaseAuth } from '../utils/firebase';
import { BILLING_API_BASE } from '../constants/config';
import { logger } from '../utils/logger';
import { getPlatform } from '../infrastructure/platform';

/**
 * Entry points for the "Upgrade to Premium" / "Manage subscription" actions,
 * backed by Stripe via the Netlify billing API (ADR-009/010/011). UI-only
 * (uses the Firebase SDK for the ID token) — do not import from the
 * background service worker.
 *
 * Flow: mint a Firebase ID token, ask the billing API for a Stripe Checkout
 * (or Billing Portal) URL, and open it in a new tab. The Netlify functions
 * verify the token server-side, so a signed-in Firebase account is required.
 * Callers must not invoke these while signed out: `UpgradeFlowProvider`
 * gates the upgrade behind account creation/sign-in (ADR-011), and the
 * manage action is only reachable by premium (hence signed-in) users.
 */

const CHECKOUT_PATH = '/api/create-checkout-session';
const PORTAL_PATH = '/api/create-billing-portal';

/**
 * Opens Stripe Checkout to start a subscription.
 * @returns true when the checkout tab was opened.
 */
export function openUpgradePage(): Promise<boolean> {
  return openBillingUrl(CHECKOUT_PATH, 'openUpgradePage');
}

/**
 * Opens the Stripe Billing Portal to manage/cancel a subscription.
 * @returns true when the portal tab was opened.
 */
export function openManagePage(): Promise<boolean> {
  return openBillingUrl(PORTAL_PATH, 'openManagePage');
}

async function openBillingUrl(path: string, caller: string): Promise<boolean> {
  const ctx = { module: 'upgradeActions', function: caller };
  try {
    const user = getFirebaseAuth().currentUser;
    if (!user) {
      logger.warning('Billing action requires a signed-in account', ctx);
      return false;
    }
    const idToken = await user.getIdToken();
    const response = await fetch(`${BILLING_API_BASE}${path}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!response.ok) {
      throw new Error(`Billing API ${path} failed (HTTP ${response.status})`);
    }
    const data = (await response.json()) as { url?: string };
    if (!data.url) {
      throw new Error(`Billing API ${path} returned no url`);
    }
    await getPlatform().actions.openExternalUrl(data.url);
    return true;
  } catch (error) {
    logger.error(
      'Failed to open Stripe billing page',
      error instanceof Error ? error : new Error(String(error)),
      ctx
    );
    return false;
  }
}
